import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Phone, Clock } from 'lucide-react';
import ProjectIcon from '../components/ProjectIcon';
import { getModule } from '../data/landing';
import './ModuleDetail.css';

const SITE = 'https://www.pidesk.in';
const CONTACT_PHONE = '8887410159';
const INCLUDED_HEADINGS = ['Key Features', "What's Included", 'Key Benefits', 'Core Capabilities'];

// Picks a genuinely different diagram for the hero, based on what the module
// actually is, instead of reusing one generic mockup shape on every page.
const getDiagram = (mod) => {
  const s = `${mod.slug} ${mod.title}`.toLowerCase();
  if (/admission|enroll|application|checkout|signup|registration/.test(s)) return 'form';
  if (/attendance|calendar|schedul|timetable|batch|class|campus/.test(s)) return 'calendar';
  if (/fee|payment|finance|revenue|invoice|checkout|pricing/.test(s)) return 'payment';
  if (/communication|notif|message|circular|alert|chat|portal/.test(s)) return 'chat';
  if (/dashboard|report|analytic|leadership|audit|oversight|seo/.test(s)) return 'dashboard';
  return 'table';
};

const Diagram = ({ type, icon }) => {
  if (type === 'form') {
    return (
      <div className="md-diagram md-diagram-form">
        <div className="df-row"><span className="df-check" /><span className="df-line w70" /></div>
        <div className="df-row"><span className="df-check" /><span className="df-line w50" /></div>
        <div className="df-row"><span className="df-check df-on" /><span className="df-line w60" /></div>
        <div className="df-input" />
        <div className="df-input" />
        <div className="df-btn">Submit</div>
      </div>
    );
  }
  if (type === 'calendar') {
    return (
      <div className="md-diagram md-diagram-calendar">
        <div className="dc-head"><ProjectIcon name={icon} size={18} /> <span>This month</span></div>
        <div className="dc-grid">
          {Array.from({ length: 21 }).map((_, i) => (
            <span key={i} className={`dc-cell ${[4, 11, 15].includes(i) ? 'dc-on' : ''}`} />
          ))}
        </div>
      </div>
    );
  }
  if (type === 'payment') {
    return (
      <div className="md-diagram md-diagram-payment">
        <div className="dp-card">
          <div className="dp-chip" />
          <div className="dp-num">•••• •••• •••• 4821</div>
          <div className="dp-row"><span>Amount</span><strong>₹ 4,500</strong></div>
        </div>
        <div className="dp-receipt"><Check size={13} /> Payment received</div>
      </div>
    );
  }
  if (type === 'chat') {
    return (
      <div className="md-diagram md-diagram-chat">
        <div className="dch-bubble dch-in">Fee due reminder sent</div>
        <div className="dch-bubble dch-out">Received, thank you</div>
        <div className="dch-bubble dch-in">Receipt generated <Check size={12} /></div>
      </div>
    );
  }
  if (type === 'dashboard') {
    return (
      <div className="md-diagram md-diagram-dashboard">
        <div className="dd-stats">
          <div><strong>128</strong><span>This week</span></div>
          <div><strong>94%</strong><span>On track</span></div>
        </div>
        <div className="dd-bars">
          <span style={{ height: '40%' }} />
          <span style={{ height: '70%' }} />
          <span style={{ height: '55%' }} />
          <span style={{ height: '90%' }} />
          <span style={{ height: '65%' }} />
        </div>
      </div>
    );
  }
  return (
    <div className="md-diagram md-diagram-table">
      <div className="dt-row dt-head"><span>Record</span><span>Status</span></div>
      <div className="dt-row"><span>Entry 1</span><span className="dt-bar w80" /></div>
      <div className="dt-row"><span>Entry 2</span><span className="dt-bar w50" /></div>
      <div className="dt-row"><span>Entry 3</span><span className="dt-bar w65" /></div>
    </div>
  );
};

// A winding, alternating-height path for topics that are fundamentally a
// sequence (admissions, enrollment, a migration or onboarding flow), used
// instead of the radial hub, which only makes sense for parallel options.
const RoadmapDiagram = ({ items }) => (
  <div className="md-roadmap" aria-hidden="true">
    <div className="md-roadmap-track" />
    {items.map((v, i) => (
      <div key={v.title} className={`md-roadmap-node ${i % 2 ? 'md-roadmap-down' : 'md-roadmap-up'}`}>
        <span className="md-roadmap-icon"><ProjectIcon name={v.icon} size={24} /></span>
        <span className="md-roadmap-label">{v.title}</span>
        <span className="md-roadmap-step">Stage {i + 1}</span>
      </div>
    ))}
  </div>
);

// A head-to-head comparison for topics that are fundamentally a choice
// between two real-world approaches (online vs offline, native vs
// cross-platform), instead of forcing three parallel nodes on a binary.
const VersusDiagram = ({ items }) => {
  const [a, b] = items;
  return (
    <div className="md-versus" aria-hidden="true">
      <div className="md-versus-side">
        <span className="md-versus-icon"><ProjectIcon name={a.icon} size={30} /></span>
        <h3>{a.title}</h3>
        <p>{a.desc}</p>
      </div>
      <span className="md-versus-badge">VS</span>
      <div className="md-versus-side md-versus-side-alt">
        <span className="md-versus-icon"><ProjectIcon name={b.icon} size={30} /></span>
        <h3>{b.title}</h3>
        <p>{b.desc}</p>
      </div>
    </div>
  );
};

const ModuleDetail = ({ landingSlug }) => {
  const { moduleSlug } = useParams();
  const { page, module: mod } = getModule(landingSlug, moduleSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [landingSlug, moduleSlug]);

  if (!page || !mod) {
    return (
      <div className="md-page">
        <div className="container md-missing">
          <h1>Module not found</h1>
          <Link to={`/${landingSlug}`} className="btn btn-primary">Back</Link>
        </div>
      </div>
    );
  }

  const url = `${SITE}/${page.slug}/${mod.slug}`;
  const siblings = page.modules.filter((m) => m.slug !== mod.slug);
  const chips = mod.bullets.slice(0, 2).map((b) => b.split(',')[0]);
  const moduleIndex = page.modules.findIndex((m) => m.slug === mod.slug);
  const includedHeading = INCLUDED_HEADINGS[moduleIndex % INCLUDED_HEADINGS.length];
  const diagramType = getDiagram(mod);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: mod.title,
    name: `${mod.title} | ${page.h1}`,
    description: mod.intro,
    provider: { '@type': 'Organization', name: 'Pidesk', url: SITE },
    areaServed: 'IN',
    url,
    isPartOf: { '@type': 'Service', name: page.h1, url: `${SITE}/${page.slug}` }
  };

  return (
    <>
      <title>{`${mod.title} | ${page.h1} | Pidesk`}</title>
      <meta name="description" content={mod.intro} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={`${mod.title} | ${page.h1}`} />
      <meta property="og:description" content={mod.intro} />
      <meta property="og:url" content={url} />
      <script type="application/ld+json">{JSON.stringify(schema)}</script>

      <div className={`md-page tone-${page.tone}`}>
        <div className="container">
          <Link to={`/${page.slug}`} className="md-breadcrumb">
            <ArrowLeft size={14} /> {page.h1}
          </Link>

          {/* ---- Split hero: text left, device-style visual right ---- */}
          <section className="md-hero">
            <div className="md-hero-text">
              <span className="md-kicker">{page.kicker} module</span>
              <h1 className="md-h1">{mod.title}</h1>
              <p className="md-intro">{mod.intro}</p>
              <div className="md-hero-actions">
                <Link to="/contact" className="btn btn-primary btn-lg">
                  Request a free demo <ArrowRight size={18} style={{ marginLeft: 8 }} />
                </Link>
                <Link to={`/${page.slug}`} className="btn btn-secondary btn-lg">
                  All {page.clusterLabel || 'modules'}
                </Link>
              </div>
            </div>
            <div className="md-hero-visual" aria-hidden="true">
              <div className="md-device">
                <div className="md-device-bar">
                  <span /><span /><span />
                </div>
                <Diagram type={diagramType} icon={mod.icon} />
              </div>
              {chips.map((c, i) => (
                <div key={c} className={`md-chip md-chip-${i}`}>
                  <Check size={13} /> {c}
                </div>
              ))}
            </div>
          </section>

          {/* ---- What is X: a direct definition, matching how people actually search ---- */}
          {mod.whatIs && (
            <section className="md-whatis">
              <span className="md-whatis-mark">?</span>
              <div>
                <h2>{mod.whatIs.q}</h2>
                <p>{mod.whatIs.a}</p>
              </div>
            </section>
          )}

          {/* ---- How it works: a real step-by-step walkthrough, specific to this module ---- */}
          {mod.how && (
            <section className="md-section">
              <h2 className="md-section-title">{mod.howHeading}</h2>
              <ol className="md-steps">
                {mod.how.map((s, i) => (
                  <li key={s.title} className="md-step">
                    <span className="md-step-num">{i + 1}</span>
                    <div>
                      <h3>{s.title}</h3>
                      <p>{s.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* ---- Variants: the diagram shape matches what the topic actually is,
               not one reused shape; the same content is repeated as readable
               cards underneath for accessibility and SEO text ---- */}
          {mod.variants && (
            <section className="md-section">
              <h2 className="md-section-title">{mod.variants.heading}</h2>

              {mod.variants.diagram === 'roadmap' && <RoadmapDiagram items={mod.variants.items} />}

              {mod.variants.diagram === 'versus' && <VersusDiagram items={mod.variants.items} />}

              {mod.variants.diagram === 'hub' && (
                <div className="md-hub" aria-hidden="true">
                  <div className="md-hub-ring" />
                  <div className="md-hub-center">{mod.title}</div>
                  {mod.variants.items.map((v, i) => (
                    <div key={v.title} className={`md-hub-node md-hub-node-${i}`}>
                      <span className="md-hub-node-icon"><ProjectIcon name={v.icon} size={24} /></span>
                      <span className="md-hub-node-label">{v.title}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="md-variant-grid">
                {mod.variants.items.map((v) => (
                  <div key={v.title} className="md-variant-card">
                    <div className="md-variant-icon"><ProjectIcon name={v.icon} size={22} /></div>
                    <h3>{v.title}</h3>
                    <p>{v.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ---- Before / after: a "before Pidesk, after Pidesk" journey, never
               a head-to-head "vs" between two things the ERP equally supports.
               The heading uses an arrow, not the word "vs", since "vs" implies a
               neutral comparison while the red X means "this is what you had
               before, not an option we are calling wrong". For modules where the
               compared options are both legitimate, ERP-supported ways of doing
               the same thing (the "versus" diagram topics), this section is
               skipped entirely instead of marking one option with a red X. ---- */}
          {mod.problems && mod.solutions && mod.variants?.diagram !== 'versus' && (
            <section className="md-section">
              <h2 className="md-section-title">
                {mod.beforeLabel} <ArrowRight size={22} className="md-challenge-title-arrow" /> {mod.afterLabel}
              </h2>
              <p className="md-challenge-subtitle">Before Pidesk, and after.</p>
              <div className="md-challenge-grid">
                <div className="md-challenge-col md-challenge-before">
                  <h3><Clock size={16} /> {mod.beforeLabel}</h3>
                  <ul>
                    {mod.problems.map((p) => <li key={p}>{p}</li>)}
                  </ul>
                </div>
                <div className="md-challenge-col md-challenge-after">
                  <h3><Check size={16} /> {mod.afterLabel}</h3>
                  <ul>
                    {mod.solutions.map((s) => <li key={s}>{s}</li>)}
                  </ul>
                </div>
              </div>
            </section>
          )}

          {/* ---- Key features / benefits (heading rotates per module, to avoid a repeated template) ---- */}
          <section className="md-section">
            <h2 className="md-section-title">{includedHeading}</h2>
            <div className="md-bullet-grid">
              {mod.bullets.map((b) => (
                <div key={b} className="md-bullet-card">
                  <span className="md-bullet-check"><Check size={14} /></span>
                  <p>{b}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ---- CTA ---- */}
          <section className="md-cta-band">
            <h2>Want to see {mod.title.toLowerCase()} in action?</h2>
            <p>We'll walk you through it live, with no obligation to buy anything.</p>
            <div className="md-cta-actions">
              <Link to="/contact" className="btn btn-primary btn-lg">
                Get a free walkthrough <ArrowRight size={18} style={{ marginLeft: 8 }} />
              </Link>
              <a href={`tel:+91${CONTACT_PHONE}`} className="btn btn-secondary btn-lg">
                <Phone size={16} style={{ marginRight: 8 }} /> Call +91 {CONTACT_PHONE}
              </a>
            </div>
          </section>

          {/* ---- Other modules on this page ---- */}
          {siblings.length > 0 && (
            <section className="md-section">
              <h2 className="md-section-title">Other modules in {page.h1}</h2>
              <div className="md-sibling-grid">
                {siblings.map((s) => (
                  <Link key={s.slug} to={`/${page.slug}/${s.slug}`} className="md-sibling-card">
                    <div className="md-sibling-icon"><ProjectIcon name={s.icon} size={20} /></div>
                    <div>
                      <h3>{s.title}</h3>
                      <p>{s.short}</p>
                    </div>
                    <ArrowRight size={16} className="md-sibling-arrow" />
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default ModuleDetail;
